/* ============================================
   Portal — Nuevo pedido
   Página de selección de servicio dentro del portal (branded), en vez de saltar
   directo a la web. Por ahora: Hosting y Dominios. El checkout real todavía vive en
   el sitio (1conecta.com); a futuro se integra aquí.
   ============================================ */

import { Link } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { usePortalCliente } from '@/hooks/usePortalCliente'
import { Breadcrumbs } from '@/components/ui'

const cardStyle: React.CSSProperties = {
  background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16,
  padding: '22px 22px 20px', height: '100%', display: 'flex', flexDirection: 'column',
  transition: 'border-color 0.15s, transform 0.15s',
}
const iconWrap: React.CSSProperties = {
  width: 44, height: 44, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center',
}
const ctaStyle: React.CSSProperties = {
  marginTop: 'auto', paddingTop: 14, fontSize: '0.85rem', fontWeight: 600, color: 'var(--info)',
}

export default function PortalNuevoPedido() {
  const { user, contactoId } = useAuth()
  const { afiliaciones, activeClienteId } = usePortalCliente()
  const activeAfil = afiliaciones.find(a => a.cliente_id === activeClienteId)
  const roles: string[] = (activeAfil?.roles as string[] | undefined) ?? user?.roles ?? []
  const puedeComprar = roles.includes('PRINCIPAL') || roles.includes('FACTURACIÓN') || !contactoId

  // Guard de rol: comprar a nombre de la empresa es acción de PRINCIPAL/FACTURACIÓN.
  if (!puedeComprar) {
    return (
      <div className="portal-page-enter" style={{ padding: '2rem' }}>
        <Link to="/portal" style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', textDecoration: 'none' }}>← Volver al resumen</Link>
        <Breadcrumbs items={[{ label: 'Portal', to: '/portal' }, { label: 'Servicios' }]} />
        <div style={{ ...cardStyle, maxWidth: 520, marginTop: 12, height: 'auto' }}>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', lineHeight: 1.6, margin: 0 }}>
            Solo los contactos <strong>PRINCIPAL</strong> o de <strong>FACTURACIÓN</strong> de la empresa pueden contratar nuevos servicios.
            Si necesitas contratar algo, pídeselo a un responsable de la empresa o{' '}
            <a href="https://wa.me/51986252983?text=Hola%2C%20quiero%20contratar%20un%20servicio" target="_blank" rel="noreferrer" style={{ color: 'var(--info)' }}>escríbenos por WhatsApp</a>.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="portal-page-enter" style={{ padding: '2rem' }}>
      <div style={{ marginBottom: 24 }}>
        <Link to="/portal" style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', textDecoration: 'none' }}>← Volver al resumen</Link>
        <Breadcrumbs items={[{ label: 'Portal', to: '/portal' }, { label: 'Servicios' }]} />
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', margin: 0 }}>¿Qué quieres contratar?</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, maxWidth: 760 }}>
        {/* Hosting */}
        <Link to="/portal/nuevo-pedido/hosting" style={{ textDecoration: 'none', color: 'inherit' }}>
          <div style={cardStyle}>
            <div style={{ ...iconWrap, background: 'color-mix(in srgb, var(--info) 12%, transparent)', color: 'var(--info)' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="2" width="20" height="8" rx="2" /><rect x="2" y="14" width="20" height="8" rx="2" /><line x1="6" y1="6" x2="6.01" y2="6" /><line x1="6" y1="18" x2="6.01" y2="18" />
              </svg>
            </div>
            <div style={{ fontWeight: 600, fontSize: '1.05rem', marginTop: 14 }}>Hosting web</div>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.83rem', lineHeight: 1.5, marginTop: 6 }}>
              Alojamiento con cPanel, SSL gratis, correos y soporte en español. Migración incluida.
            </p>
            <span style={ctaStyle}>Ver planes →</span>
          </div>
        </Link>

        {/* Dominios — por ahora entra por el checkout de hosting con el paso de dominio */}
        <Link to="/portal/nuevo-pedido/hosting?dominio=nuevo" style={{ textDecoration: 'none', color: 'inherit' }}>
          <div style={cardStyle}>
            <div style={{ ...iconWrap, background: 'color-mix(in srgb, var(--success) 12%, transparent)', color: 'var(--success)' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
              </svg>
            </div>
            <div style={{ fontWeight: 600, fontSize: '1.05rem', marginTop: 14 }}>Dominios</div>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.83rem', lineHeight: 1.5, marginTop: 6 }}>
              Registra tu dominio .com, .net, .org y más. Con un plan anual, tu .com el primer año.
            </p>
            <span style={{ ...ctaStyle, color: 'var(--success)' }}>Buscar dominio →</span>
          </div>
        </Link>
      </div>

      <p style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: 20 }}>
        ¿No encuentras lo que buscas? <a href="https://wa.me/51986252983?text=Hola%2C%20quiero%20contratar%20un%20servicio" target="_blank" rel="noreferrer" style={{ color: 'var(--info)' }}>Escríbenos por WhatsApp</a> y te ayudamos.
      </p>
    </div>
  )
}
